/**
 * ONE WebSocket to /ws/market, shared by every consumer.
 *
 * WHY THIS EXISTS — measured on production 2026-08-14:
 *
 * Five hooks each opened their OWN socket to the same URL
 * (useMarketData, useRealTimeAlerts, useInstitutionalSignals,
 * usePortfolioUpdates, useLiveForecast) and each hand-rolled its own
 * reconnect policy. The server caps a client at 6 concurrent connections
 * (MAX_CONNECTIONS_PER_IP, server/websocket.ts:799) — its comment says
 * "one per known WS connection type per client" — so a single tab sat at 5
 * of 6 with no headroom, and any reconnect that overlapped an existing
 * socket crossed the cap.
 *
 * All four hand-rolled policies shared the same defect: `onopen` reset the
 * attempt counter, and the server ACCEPTS the upgrade before closing it with
 * 1008. So backoff never accumulated, the attempt cap was never reached, and
 * the retries were themselves what breached the rate limit they were being
 * rejected for. Result: 16 upgrades in 30 seconds from one tab against a
 * limit of 12 per 60s, and a realtime feed that was permanently down while
 * the UI said nothing.
 *
 * Nothing was lost by sharing: no consumer has ever SENT on this socket
 * (`grep '\.send(' client/hooks/`), every one just filters inbound messages
 * by `type`, and two of them (useRealTimeAlerts, useInstitutionalSignals)
 * already consumed the SAME message types over two separate sockets. Fanning
 * one socket out to N listeners is exactly what they were each doing alone.
 */

export type MarketSocketStatus = "connecting" | "connected" | "disconnected";

type MessageListener = (msg: any) => void;
type StatusListener = (status: MarketSocketStatus) => void;

/** A connection must hold this long before we believe the endpoint is healthy. */
const STABLE_CONNECTION_MS = 10_000;
/** 1008 = Policy Violation. Retrying fast is what breached the policy. */
const POLICY_CLOSE_CODE = 1008;
const POLICY_RETRY_FLOOR_MS = 45_000;
const BASE_RETRY_MS = 3_000;
const MAX_RETRY_MS = 60_000;
const MAX_ATTEMPTS = 8;
/**
 * Closing the moment the last listener leaves would tear the socket down and
 * rebuild it on every screen transition — the churn this module exists to
 * remove. Hold it briefly instead.
 */
const IDLE_CLOSE_GRACE_MS = 5_000;

const messageListeners = new Set<MessageListener>();
const statusListeners = new Set<StatusListener>();

let socket: WebSocket | null = null;
let socketUrl: string | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let attempts = 0;
let openedAt = 0;
let closedByUs = false;
let status: MarketSocketStatus = "disconnected";

function setStatus(next: MarketSocketStatus): void {
  if (status === next) return;
  status = next;
  statusListeners.forEach((fn) => {
    try {
      fn(next);
    } catch {
      /* a broken listener must not take the socket down */
    }
  });
}

function clearRetry(): void {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

function open(url: string): void {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }
  socketUrl = url;
  setStatus("connecting");

  let ws: WebSocket;
  try {
    ws = new WebSocket(url);
  } catch {
    setStatus("disconnected");
    return;
  }
  socket = ws;
  closedByUs = false;

  ws.onopen = () => {
    openedAt = Date.now();
    setStatus("connected");
    // Deliberately NOT resetting `attempts` here. Opening is not succeeding:
    // the server accepts the upgrade and then closes with 1008.
  };

  ws.onmessage = (event) => {
    let parsed: any;
    try {
      parsed = JSON.parse(event.data);
    } catch {
      return; // malformed frame — silent, as every hook already did
    }
    messageListeners.forEach((fn) => {
      try {
        fn(parsed);
      } catch {
        /* one consumer's error must not starve the others */
      }
    });
  };

  ws.onerror = () => {
    // Close handling owns the retry; onerror fires alongside it.
  };

  ws.onclose = (event: CloseEvent) => {
    socket = null;
    setStatus("disconnected");
    if (closedByUs) return;

    const heldFor = openedAt ? Date.now() - openedAt : 0;
    openedAt = 0;
    // Only a connection that actually SURVIVED earns a fresh budget.
    if (heldFor >= STABLE_CONNECTION_MS) attempts = 0;

    if (messageListeners.size === 0 && statusListeners.size === 0) return;
    if (attempts >= MAX_ATTEMPTS) return;

    const ladder = Math.min(BASE_RETRY_MS * Math.pow(2, attempts), MAX_RETRY_MS);
    const base = event?.code === POLICY_CLOSE_CODE ? Math.max(ladder, POLICY_RETRY_FLOOR_MS) : ladder;
    // Jitter, so tabs woken by the same event do not retry in lockstep and
    // manufacture the burst the server is rate-limiting.
    const delay = Math.round(base * (0.7 + Math.random() * 0.6));

    attempts++;
    clearRetry();
    retryTimer = setTimeout(() => socketUrl && open(socketUrl), delay);
  };
}

function closeNow(): void {
  clearRetry();
  if (socket) {
    closedByUs = true;
    socket.close();
    socket = null;
  }
  attempts = 0;
  setStatus("disconnected");
}

export interface MarketSocketSubscription {
  onMessage?: MessageListener;
  onStatus?: StatusListener;
}

/**
 * Attach to the shared socket, opening it if this is the first subscriber.
 * Returns the detach function — call it from the effect's cleanup.
 */
export function subscribeMarketSocket(url: string, sub: MarketSocketSubscription): () => void {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  if (sub.onMessage) messageListeners.add(sub.onMessage);
  if (sub.onStatus) {
    statusListeners.add(sub.onStatus);
    sub.onStatus(status); // hand the newcomer the current state, not a stale default
  }

  open(url);

  return () => {
    if (sub.onMessage) messageListeners.delete(sub.onMessage);
    if (sub.onStatus) statusListeners.delete(sub.onStatus);
    if (messageListeners.size === 0 && statusListeners.size === 0) {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        idleTimer = null;
        if (messageListeners.size === 0 && statusListeners.size === 0) closeNow();
      }, IDLE_CLOSE_GRACE_MS);
    }
  };
}

/** Test seam: current listener/socket state. Not for render logic. */
export function __marketSocketState() {
  return {
    listeners: messageListeners.size,
    statusListeners: statusListeners.size,
    status,
    attempts,
    hasSocket: socket != null,
  };
}
